// src/com/sms_message/SmsMessageSender.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, CircularProgress, AppBar, Tab, Tabs
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { AppWord, AppObject, AppStyle, InputType } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import VzmTextareaField from '../../common/VzmTextareaField';
import { ErrorHintBox } from '../../common/VzmStyled';
import InputUtil from '../../value/InputUtil';
import { SmsMessageDictionary } from './SmsMessageDict';
import VehInfoListForSelection from '../veh_info/VehInfoListForSelection';
import SmsPhrases from '../sms_phrases/SmsPhrases';
import { ContentHeader } from '../../common/TopViewControl';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { enqueueSnackbar } from 'notistack';
import IsValid from '../../value/IsValid';

const InputMap = SmsMessageDictionary.inputMap;

// InputBox at left.
const InputBox = styled(Box)({
    width: 360,
    display: 'flex', flexDirection: 'column',
});

const PhraseAndTrucksContainer = styled(Box)({
    flexGrow: 1, minWidth: 580, // backgroundColor: 'lightblue'
    display: 'flex', flexDirection: 'column',
    marginLeft: 10,
});

const PhraseOrTruckBox = styled(Box)({
    flexGrow: 1, // backgroundColor: 'lightblue'
});

const AssistTab = { trucks:1,phrase:2, };

const ReceiverTableProps = SmsMessageDictionary.receiverTableProps;

/**
 * 문자전송용 Dialog
 * SMS 전송 기록
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-01-24 13:47:18 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function SmsMessageSender({
    open,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [tabSelected, setTabSelected] = useState(AssistTab.trucks);
    const [phoneAdding, setPhoneAdding] = useState('');
    const [wrongPhone, setWrongPhone] = useState(false);
    const [receivers, setReceivers] = useState([]); // {name, phone}
    const openConfirmDialog = useConfirmDialog();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            inputSet.data.message = '';
            let editing = false;
            setIsEdit(editing);
            setInput(inputSet);
            setReceivers([]);
        }
    }, [open]);

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        //const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        const param = {
            message: input.data.message,
            receiveTelList: receivers.map((rec)=>rec.phone),
        }
        onRequestSave(param);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const onSelectPhrase = (phrase) => {
        const newInput = {...input};
        newInput.data.message = phrase;
        newInput.changed = true;
        newInput.error = {};
        newInput.hasError = false;
        newInput.errorLabels = [];
        setInput(newInput);
    };

    const onSelectTruck = (truck) => {
        if(truck.driverMobile) {
            const phone = truck.driverMobile.replace(/\D/g, '');
            if(IsValid.mobile(phone)) {
                const rec = {
                    phone,
                    name: truck.vehPlates + ' ' + truck.driverName,
                };
                // check if phone is already in the list
                const found = receivers.find((rec)=>rec.phone === phone);
                if(!found) {
                    const newList = [...receivers];
                    newList.push(rec);
                    setReceivers(newList);
                }
                else {
                    enqueueSnackbar('이미 추가한 전화번호입니다.', {variant:'warning', autoHideDuration:2000});
                }
            }
            else {
                enqueueSnackbar('유효하지 않은 전화번호입니다.', {variant:'warning', autoHideDuration:2000});
            }
        }
    };

    const addPhoneNumber = (phone) => {
        if(wrongPhone) return;

        // check if phone is already in the list
        const found = receivers.find((rec)=>rec.phone === phone);
        if(found) {
            enqueueSnackbar('이미 추가된 전화번호입니다.', {variant:'warning', autoHideDuration:2000});
            return;
        }
        const newReceivers = [...receivers];
        newReceivers.push({name:phone, phone});
        setReceivers(newReceivers);
        const newInput = {...input};
        newInput.data.receiveTelList = newReceivers.map((rec)=>rec.phone);
        newInput.changed = true;
        setInput(newInput);
    };

    const removeReceiver = (item) => {
        const newReceivers = receivers.filter((rec)=>rec.phone !== item.phone);
        setReceivers(newReceivers);
    };
    
    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: removeReceiver(row); break;
            default: return;
        }
    };

    const tools = [{id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning}];

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='lg'
            onClose={onClickCancel}
            title='문자전송'
        >
            <DialogContent style={{height: '75vh', display: 'flex'}}>
                <InputBox>
                    <ContentHeader
                        style={{
                            backgroundColor: AppStyle.PrimaryRGB, color: 'white',
                            borderTopLeftRadius: 5, borderTopRightRadius: 5,
                            paddingLeft: 10
                        }}
                    >
                        수신자 목록
                    </ContentHeader>

                    <FlexyTable
                        name={ReceiverTableProps.tableId}
                        tableProps={ReceiverTableProps}
                        records={receivers}
                        tools={tools}
                        onClickTool={onClickToolOnRow}
                        usePopoverBubble={true}
                    />
                    <Box style={{display:'flex', alignItems:'center', marginTop:10}}>
                        <Box style={{flexGrow:1}}>
                            <VzmTextField
                                dict={{
                                    id: 'phone', label: '수신번호 추가', type: InputType.text,
                                    required: true, toAdd: true, toEdit: true,
                                    check: (value) => IsValid.mobile(value)
                                }}
                                value={phoneAdding}
                                onChange={(id, value, error)=>{
                                    setPhoneAdding(value);
                                    setWrongPhone(error);
                                }}
                                fullWidth={true}
                                onEnterKey={()=>addPhoneNumber(phoneAdding)}
                            />
                        </Box>
                        <Button
                            onClick={()=>addPhoneNumber(phoneAdding)}
                            disabled={wrongPhone}
                            style={{marginLeft:10}}
                            color={AppStyle.mui.primary}
                            startIcon={<AddIcon />}
                            variant={AppStyle.mui.contained}
                        >
                            추가
                        </Button>
                    </Box>


                    <Box style={{marginTop:10}}>
                        <VzmTextareaField
                            dict={InputMap.message}
                            value={input.data.message}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Box>
                    <Box style={{marginTop:10}}>
                        <Button
                            color={AppStyle.mui.primary}
                            variant={AppStyle.mui.contained}
                            fullWidth={true}
                            disabled={
                                input.hasError || !input.changed || receivers.length === 0
                                || !input.data.message || input.data.message.length < 1
                                || loadingSave
                            }
                            onClick={onClickSubmit}
                        >
                            전송
                        </Button>
                    </Box>
                </InputBox>
                <PhraseAndTrucksContainer>
                    <AppBar position='static' color='default'>
                        <Tabs value={tabSelected} onChange={(e,v)=>setTabSelected(v)}>
                            <Tab value={AssistTab.trucks} label='차량 클릭해서 수신자에 추가' />
                            <Tab value={AssistTab.phrase} label='자주쓰는 문구' />
                        </Tabs>
                    </AppBar>
                    <PhraseOrTruckBox>
                        {
                            tabSelected === AssistTab.trucks
                            ?
                            <VehInfoListForSelection onSelected={onSelectTruck}/>
                            :
                            <SmsPhrases onSelected={onSelectPhrase}/>
                        }
                    </PhraseOrTruckBox>
                </PhraseAndTrucksContainer>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;문저 전송 요청 중...
                    </span>
                    :
                    <>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >닫기</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
